import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import {
  Shield,
  Users,
  Wallet,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Check,
  Save,
  X,
  SlidersHorizontal,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ----------------------------------------------------------------
// Roles & Permissions (RBAC)
// ----------------------------------------------------------------
// Two tabs:
//   - Roles:  edit the permission set granted by each role.
//   - People: assign a role to each admin/staff user and manage
//             per-user permission overrides (grant / revoke).
// Backed by the /admin/roles, /admin/permissions, /admin/admins and
// /admin/users/:id/* endpoints.

const ALL = '*'; // sentinel: a role's permissions = ["*"] means full access

// Build a flat list of every permission key from the grouped catalog.
const flattenKeys = (groups) =>
  (groups || []).flatMap((g) => (g.permissions || []).map((p) => p.key));

export default function Roles() {
  const [tab, setTab] = useState('roles');

  // Shared data
  const [groups, setGroups] = useState([]); // permission catalog
  const [roles, setRoles] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [cageStaff, setCageStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCore = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [permRes, roleRes] = await Promise.all([
        api.get('/admin/permissions'),
        api.get('/admin/roles'),
      ]);
      setGroups(permRes.data?.groups || []);
      setRoles(roleRes.data?.roles || []);
    } catch (err) {
      setError(
        err?.response?.data?.error || 'Failed to load roles & permissions'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAdmins = useCallback(async () => {
    try {
      const res = await api.get('/admin/admins');
      setAdmins(res.data?.admins || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load users');
    }
  }, []);

  const loadCageStaff = useCallback(async () => {
    try {
      const res = await api.get('/admin/cage-roles');
      setCageStaff(res.data?.staff || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load cage roles');
    }
  }, []);

  const refetchRoles = useCallback(async () => {
    try {
      const res = await api.get('/admin/roles');
      setRoles(res.data?.roles || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to refresh roles');
    }
  }, []);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  useEffect(() => {
    if (tab === 'people') loadAdmins();
  }, [tab, loadAdmins]);

  useEffect(() => {
    if (tab === 'cage') loadCageStaff();
  }, [tab, loadCageStaff]);

  return (
    <div className="p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
          <Shield className="text-amber-500" size={26} />
          Roles &amp; Permissions
        </h1>
        <p className="text-gray-400 text-sm mt-1">
          Control what each role can do and who has it.
        </p>
      </header>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700">
        <TabButton
          active={tab === 'roles'}
          onClick={() => setTab('roles')}
          icon={Shield}
          label="Roles"
        />
        <TabButton
          active={tab === 'people'}
          onClick={() => setTab('people')}
          icon={Users}
          label="People"
        />
        <TabButton
          active={tab === 'cage'}
          onClick={() => setTab('cage')}
          icon={Wallet}
          label="Cage Roles"
        />
      </div>

      {loading ? (
        <div className="p-8 text-gray-400 flex items-center gap-3">
          <RefreshCw className="animate-spin" size={18} /> Loading...
        </div>
      ) : error ? (
        <div className="bg-red-900/30 border border-red-700/50 rounded-lg px-4 py-3 text-red-200 text-sm">
          {error}
        </div>
      ) : tab === 'roles' ? (
        <RolesTab
          roles={roles}
          groups={groups}
          onSaved={refetchRoles}
        />
      ) : tab === 'people' ? (
        <PeopleTab
          admins={admins}
          roles={roles}
          groups={groups}
          reloadAdmins={loadAdmins}
        />
      ) : (
        <CageRolesTab staff={cageStaff} reload={loadCageStaff} />
      )}
    </div>
  );
}

// ----------------------------------------------------------------
// Tab button
// ----------------------------------------------------------------
function TabButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium flex items-center gap-2 -mb-px border-b-2 ${
        active
          ? 'border-amber-500 text-amber-400'
          : 'border-transparent text-gray-400 hover:text-gray-200'
      }`}
    >
      <Icon size={16} />
      {label}
    </button>
  );
}

// ================================================================
// Roles tab
// ================================================================
function RolesTab({ roles, groups, onSaved }) {
  const [expandedId, setExpandedId] = useState(null);

  if (!roles.length) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 text-gray-500 text-sm">
        No roles defined.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {roles.map((role) => (
        <RoleCard
          key={role.id}
          role={role}
          groups={groups}
          expanded={expandedId === role.id}
          onToggle={() =>
            setExpandedId((id) => (id === role.id ? null : role.id))
          }
          onSaved={onSaved}
        />
      ))}
    </div>
  );
}

function RoleCard({ role, groups, expanded, onToggle, onSaved }) {
  const isOwner = (role.permissions || []).includes(ALL);
  const allKeys = flattenKeys(groups);

  // Local checkbox state — initialized from the role on each expand.
  const [checked, setChecked] = useState(() => new Set(role.permissions || []));
  const [saving, setSaving] = useState(false);

  // Re-sync local state whenever the card is (re)expanded or the role changes.
  useEffect(() => {
    if (expanded) {
      setChecked(
        isOwner ? new Set(allKeys) : new Set(role.permissions || [])
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, role]);

  const permCount = isOwner ? allKeys.length : (role.permissions || []).length;

  const toggleKey = (key) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleGroup = (groupKeys, allOn) => {
    setChecked((prev) => {
      const next = new Set(prev);
      groupKeys.forEach((k) => (allOn ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/roles/${role.id}`, {
        permissions: Array.from(checked),
      });
      toast.success(`Saved "${role.name}"`);
      await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
      {/* Header row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-700/20"
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown size={18} className="text-gray-400" />
          ) : (
            <ChevronRight size={18} className="text-gray-400" />
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="text-white font-medium">{role.name}</span>
              {role.isSystem && (
                <span className="text-[10px] uppercase tracking-wide bg-gray-700 text-gray-300 px-2 py-0.5 rounded">
                  System
                </span>
              )}
            </div>
            {role.description && (
              <div className="text-sm text-gray-400 mt-0.5">
                {role.description}
              </div>
            )}
          </div>
        </div>
        <span className="text-sm text-gray-400 whitespace-nowrap ml-3">
          {permCount} permission{permCount === 1 ? '' : 's'}
        </span>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-gray-700 p-5 space-y-5">
          {isOwner && (
            <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg px-4 py-2 text-amber-200 text-sm">
              Owner always has full access.
            </div>
          )}

          {groups.length === 0 ? (
            <div className="text-gray-500 text-sm">
              No permissions in the catalog.
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2">
              {groups.map((group) => {
                const groupKeys = (group.permissions || []).map((p) => p.key);
                const allOn =
                  groupKeys.length > 0 &&
                  groupKeys.every((k) => checked.has(k));
                return (
                  <div
                    key={group.key}
                    className="bg-gray-900 border border-gray-700 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs uppercase tracking-wide text-gray-400 font-medium">
                        {group.label}
                      </h3>
                      {!isOwner && groupKeys.length > 0 && (
                        <button
                          onClick={() => toggleGroup(groupKeys, allOn)}
                          className="text-xs text-amber-400 hover:text-amber-300"
                        >
                          {allOn ? 'Clear all' : 'Select all'}
                        </button>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      {(group.permissions || []).map((perm) => (
                        <label
                          key={perm.key}
                          className={`flex items-center gap-2 text-sm ${
                            isOwner
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-200 cursor-pointer'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked.has(perm.key)}
                            disabled={isOwner}
                            onChange={() => toggleKey(perm.key)}
                            className="accent-amber-500 w-4 h-4"
                          />
                          {perm.label}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Save */}
          {!isOwner && (
            <div className="flex justify-end">
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                Save changes
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ================================================================
// People tab
// ================================================================
function PeopleTab({ admins, roles, groups, reloadAdmins }) {
  const [overridesUser, setOverridesUser] = useState(null);

  const roleById = (id) => roles.find((r) => r.id === id) || null;

  const changeRole = async (user, roleId) => {
    try {
      await api.put(`/admin/users/${user.id}/role`, {
        roleId: roleId || null,
      });
      const r = roleById(roleId);
      toast.success(
        `${user.firstName || user.email} → ${r ? r.name : 'No role'}`
      );
      await reloadAdmins();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update role');
    }
  };

  if (!admins.length) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 text-gray-500 text-sm">
        No admin or staff users found.
      </div>
    );
  }

  return (
    <>
      <section className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-900">
              <tr className="text-left text-gray-400">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium text-right">Overrides</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((user) => {
                const name =
                  [user.firstName, user.lastName].filter(Boolean).join(' ') ||
                  user.email;
                return (
                  <tr
                    key={user.id}
                    className="border-t border-gray-700/60 hover:bg-gray-700/20"
                  >
                    <td className="px-4 py-2.5 text-white">{name}</td>
                    <td className="px-4 py-2.5 text-gray-400">{user.email}</td>
                    <td className="px-4 py-2.5">
                      <select
                        value={user.roleId || ''}
                        onChange={(e) => changeRole(user, e.target.value)}
                        className="px-3 py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
                      >
                        <option value="">— No role —</option>
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => setOverridesUser(user)}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg inline-flex items-center gap-2"
                      >
                        <SlidersHorizontal size={14} />
                        Overrides
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {overridesUser && (
        <OverridesModal
          user={overridesUser}
          groups={groups}
          onClose={() => setOverridesUser(null)}
        />
      )}
    </>
  );
}

// ----------------------------------------------------------------
// Per-user permission overrides modal
// ----------------------------------------------------------------
function OverridesModal({ user, groups, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState(null); // { id, key, name, permissions: [] }
  // state: Map<permissionKey, 'inherit' | 'grant' | 'revoke'>
  const [state, setState] = useState(new Map());

  const userName =
    [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/users/${user.id}/permissions`);
        if (cancelled) return;
        setRole(res.data?.role || null);
        const map = new Map();
        (res.data?.overrides || []).forEach((o) => {
          map.set(o.permissionKey, o.effect); // 'grant' | 'revoke'
        });
        setState(map);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err?.response?.data?.error || 'Failed to load overrides'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  const rolePerms = new Set(role?.permissions || []);
  const ownerRole = rolePerms.has(ALL);

  const setEffect = (key, effect) => {
    setState((prev) => {
      const next = new Map(prev);
      if (effect === 'inherit') next.delete(key);
      else next.set(key, effect);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const overrides = Array.from(state.entries()).map(
        ([permissionKey, effect]) => ({ permissionKey, effect })
      );
      await api.put(`/admin/users/${user.id}/permissions`, { overrides });
      toast.success(`Overrides saved for ${userName}`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save overrides');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-white">
              Permission overrides
            </h2>
            <p className="text-sm text-gray-400 mt-0.5">
              {userName}
              {role ? (
                <>
                  {' '}
                  · role:{' '}
                  <span className="text-gray-300">{role.name}</span>
                </>
              ) : (
                <span className="text-gray-500"> · no role assigned</span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5 space-y-5">
          {loading ? (
            <div className="text-gray-400 flex items-center gap-3">
              <RefreshCw className="animate-spin" size={18} /> Loading
              overrides...
            </div>
          ) : groups.length === 0 ? (
            <div className="text-gray-500 text-sm">
              No permissions in the catalog.
            </div>
          ) : (
            <>
              <div className="text-xs text-gray-400">
                <span className="inline-flex items-center gap-1 mr-3">
                  <Check size={12} className="text-emerald-400" /> Role grants
                  this
                </span>
                "Inherit" follows the role. Grant / Revoke override it.
              </div>
              {groups.map((group) => (
                <div
                  key={group.key}
                  className="bg-gray-900 border border-gray-700 rounded-lg p-4"
                >
                  <h3 className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-3">
                    {group.label}
                  </h3>
                  <div className="space-y-2">
                    {(group.permissions || []).map((perm) => {
                      const current = state.get(perm.key) || 'inherit';
                      const inherited =
                        ownerRole || rolePerms.has(perm.key);
                      return (
                        <div
                          key={perm.key}
                          className="flex items-center justify-between gap-3"
                        >
                          <span className="text-sm text-gray-200 flex items-center gap-1.5">
                            {inherited && (
                              <Check
                                size={13}
                                className="text-emerald-400 shrink-0"
                              />
                            )}
                            {perm.label}
                          </span>
                          <ThreeWay
                            value={current}
                            onChange={(v) => setEffect(perm.key, v)}
                            inheritHint={inherited ? 'Granted' : 'Denied'}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || loading}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center gap-2 text-sm"
          >
            {saving ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            )}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// 3-way segmented control: Inherit / Grant / Revoke
function ThreeWay({ value, onChange, inheritHint }) {
  const options = [
    { key: 'inherit', label: 'Inherit', active: 'bg-gray-600 text-white' },
    { key: 'grant', label: 'Grant', active: 'bg-emerald-600 text-white' },
    { key: 'revoke', label: 'Revoke', active: 'bg-red-600 text-white' },
  ];
  return (
    <div className="flex rounded-lg overflow-hidden border border-gray-700 shrink-0">
      {options.map((opt) => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          title={
            opt.key === 'inherit' && inheritHint
              ? `Inherit (${inheritHint})`
              : undefined
          }
          className={`px-2.5 py-1 text-xs ${
            value === opt.key
              ? opt.active
              : 'bg-gray-900 text-gray-400 hover:text-gray-200'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ================================================================
// Cage Roles tab
// ================================================================
// The cage flow re-verifies a staff PIN per wallet action and requires
// that PIN's cage_role to be banker or supervisor. This is separate
// from the RBAC role above: it attaches to the employee number + PIN
// punched in at the cage, not to the dashboard login.
function CageRolesTab({ staff, reload }) {
  const [savingId, setSavingId] = useState(null);

  const setCageRole = async (member, cageRole) => {
    setSavingId(member.id);
    try {
      await api.put(
        `/admin/cage-roles/${encodeURIComponent(member.employeeNumber)}`,
        { cageRole: cageRole || null }
      );
      toast.success(`${member.employeeName} → ${cageRole || 'no cage role'}`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update cage role');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-gray-800/60 border border-gray-700 rounded-lg px-4 py-3 text-sm text-gray-400">
        Cage roles control who can act as a banker at the Player Bank cage. They
        apply to the employee number + PIN entered at the cage, separately from
        a person&apos;s dashboard role.{' '}
        <span className="text-gray-300">Banker</span> can post deposits,
        withdrawals and same-shift voids;{' '}
        <span className="text-gray-300">Supervisor</span> adds adjustments,
        freezes and post-shift voids.
      </div>

      {!staff.length ? (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 text-gray-500 text-sm">
          No staff PINs found.
        </div>
      ) : (
        <section className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-900">
                <tr className="text-left text-gray-400">
                  <th className="px-4 py-3 font-medium">Employee</th>
                  <th className="px-4 py-3 font-medium">Emp #</th>
                  <th className="px-4 py-3 font-medium">Department</th>
                  <th className="px-4 py-3 font-medium">Cage role</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((member) => (
                  <tr
                    key={member.id}
                    className="border-t border-gray-700/60 hover:bg-gray-700/20"
                  >
                    <td className="px-4 py-2.5 text-white">
                      {member.employeeName}
                    </td>
                    <td className="px-4 py-2.5 text-gray-400">
                      {member.employeeNumber}
                    </td>
                    <td className="px-4 py-2.5 text-gray-400">
                      {member.department || '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <select
                        value={member.cageRole || ''}
                        disabled={savingId === member.id}
                        onChange={(e) => setCageRole(member, e.target.value)}
                        className="px-3 py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500 disabled:opacity-50"
                      >
                        <option value="">— None —</option>
                        <option value="banker">Banker</option>
                        <option value="supervisor">Supervisor</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
